import { Vector3 } from 'three';
import { BALANCE } from '../config/balance';
import { burstSpread, difficultyProfile, type DifficultyProfile } from '../config/difficulty';
import type { Rng } from '../core/Rng';
import type { Surface } from '../world/Surface';
import { Health } from '../combat/Health';
import { wrapAngle } from '../utils/math';

const scratch = new Vector3();
const scratchOrbit = new Vector3();
const scratchJitterRight = new Vector3();
const UP = new Vector3(0, 1, 0);

/**
 * A hovering drone. Drones are the spatial-ambush threat: they sit on
 * surfaces the player is not facing, so finding them is part of the game.
 *
 * Each drone owns its own fire cadence. The first shot is delayed by the
 * engagement grace period and then staggered per drone, so the round never
 * opens with every drone shooting in the same frame.
 */
export class Drone {
  readonly position = new Vector3();
  readonly anchor = new Vector3();
  readonly radius = 0.45;
  readonly health: Health;

  private readonly phase: number;
  private readonly bobSpeed: number;
  private readonly bobAmount: number;
  private readonly patrolPhase: number;
  private readonly patrolSpeed: number;
  /** Accumulated orbit angle so a speed change never teleports the drone. */
  private patrolAngle = 0;
  private patrolRadius: number;
  private readonly drift: Vector3;
  /** The authored patrol loop, in world metres, or null for a free orbit. */
  private route: Vector3[] | null;
  /** The waypoint the drone is flying toward. */
  private routeIndex = 0;
  private destroyed = false;
  /** When held, the drone stays exactly where it was pinned. */
  private held = false;
  /** Seconds left under a capture net; zero means free. */
  private netTimer = 0;
  /** Magnetic drones can be pulled by the grapple hook. */
  readonly magnetic: boolean;

  /** Combat behaviour: watching, waking up, firing a burst, or cooling down. */
  private state: 'patrol' | 'alert' | 'engage' | 'rest' = 'rest';
  /** True while the target was visible on the last frame. */
  private targetSeen = false;
  /** Shots left in the current burst. */
  private burstShots = 0;
  /** Shots the current burst started with, so the spread can tighten along it. */
  private burstLength = 0;
  /** The spread the next shot will use, radians. */
  private shotSpread: number;
  /** Seconds until the next shot inside a burst. */
  private burstTimer = 0;
  /** Seconds left in the current rest. */
  private restTimer = 0;
  /** Seconds until the target wakes the drone up. */
  private reactTimer = 0;
  /** Extra pause once a lost target is seen again. */
  private reacquireTimer = 0;
  /** Seconds left while a heard noise keeps the drone suspicious. */
  private suspicionTimer = 0;

  constructor(
    readonly id: string,
    anchor: Vector3,
    readonly surface: Surface | null,
    rng: Rng,
    /** A separate deterministic stream so firing never disturbs the appearance roll. */
    private readonly fireRng: Rng = rng,
    magnetic = false,
    /** The level's difficulty tier; the drone's fire cadence and spread. */
    private readonly profile: DifficultyProfile = difficultyProfile('standard'),
    /** An authored patrol loop; with two or more points it replaces the orbit. */
    route?: ReadonlyArray<readonly [number, number, number]>,
  ) {
    this.magnetic = magnetic;
    this.shotSpread = profile.jitterStart;
    this.anchor.copy(anchor);
    this.position.copy(anchor);
    this.health = new Health(BALANCE.combat.droneHealth);
    this.route = route && route.length >= 2 ? route.map(([x, y, z]) => new Vector3(x, y, z)) : null;
    this.phase = rng.range(0, Math.PI * 2);
    this.bobSpeed = rng.range(BALANCE.combat.droneBobSpeedMin, BALANCE.combat.droneBobSpeedMax);
    this.bobAmount = rng.range(BALANCE.combat.droneBobAmountMin, BALANCE.combat.droneBobAmountMax);
    this.patrolPhase = rng.range(0, Math.PI * 2);
    this.patrolAngle = this.patrolPhase;
    this.patrolSpeed =
      rng.range(BALANCE.combat.dronePatrolSpeedMin, BALANCE.combat.dronePatrolSpeedMax) *
      (rng.next() < 0.5 ? -1 : 1);
    const drift = BALANCE.combat.droneDrift;
    this.drift = new Vector3(
      rng.range(-drift, drift),
      rng.range(-drift, drift),
      rng.range(-drift, drift),
    );

    // Keep the patrol loop inside the surface bounds so a drone never drifts
    // through a wall or off the edge of its anchor surface.
    let edgeRoom = BALANCE.combat.dronePatrolRadiusMax + BALANCE.combat.droneEdgeMargin;
    if (surface) {
      const local = surface.toLocal(anchor);
      edgeRoom = Math.min(
        local.u - surface.uMin,
        surface.uMax - local.u,
        local.v - surface.vMin,
        surface.vMax - local.v,
      );
    }
    this.patrolRadius = Math.max(
      0,
      Math.min(
        rng.range(BALANCE.combat.dronePatrolRadiusMin, BALANCE.combat.dronePatrolRadiusMax),
        edgeRoom - BALANCE.combat.droneEdgeMargin,
      ),
    );
    // A route replaces the orbit entirely: the anchor itself travels, so the
    // orbit offset has to stay at zero or the drone would double up on motion.
    if (this.route) this.patrolRadius = 0;

    // Stagger the first burst inside the engagement window so the drones do
    // not wake in unison.
    this.restTimer =
      BALANCE.combat.droneEngageDelay + this.fireRng.range(0, this.profile.burstRest);
  }

  get isDestroyed(): boolean {
    return this.destroyed || this.health.isDestroyed;
  }

  /** True while a capture net still holds the drone. */
  get isNetted(): boolean {
    return this.netTimer > 0;
  }

  /** True while the drone is awake because it saw or heard the player. */
  get isAlerted(): boolean {
    return this.suspicionTimer > 0 || this.state === 'alert' || this.state === 'engage';
  }

  /** True while the drone is actively firing a burst, so bolts are incoming. */
  get isEngaging(): boolean {
    return this.state === 'engage' && !this.destroyed;
  }

  /** Seconds of suspicion left after hearing a noise, for tests and the HUD. */
  get suspicionRemaining(): number {
    return this.suspicionTimer;
  }

  /** The authored patrol loop, or null when the drone flies a free orbit. */
  get patrolRoute(): readonly Vector3[] | null {
    return this.route;
  }

  /** The waypoint the drone is heading for, for tests and the HUD. */
  get nextWaypoint(): number {
    return this.routeIndex;
  }

  /**
   * A noise reached the drone. It becomes suspicious and wakes from its rest,
   * but it still needs line of sight before it can fire.
   */
  hearNoise(): boolean {
    if (this.isDestroyed || this.isNetted) return false;
    this.suspicionTimer = Math.max(this.suspicionTimer, BALANCE.stealth.hearingMemory);
    if (!this.targetSeen && (this.state === 'rest' || this.state === 'patrol')) {
      this.state = 'alert';
      this.restTimer = 0;
      this.reactTimer = BALANCE.stealth.hearingReaction;
    }
    return true;
  }

  /** Seconds left under the net, for tests and the view. */
  get netRemaining(): number {
    return this.netTimer;
  }

  /** Wrap the drone in a capture net for `duration` seconds. */
  net(duration: number): void {
    if (this.isDestroyed) return;
    this.netTimer = Math.max(this.netTimer, duration);
  }

  /** Reel a magnetic drone toward a point by at most `distance` metres. */
  pullToward(target: Vector3, distance: number): void {
    if (this.isDestroyed) return;
    scratch.subVectors(target, this.anchor);
    const length = scratch.length();
    if (length < 1e-4) return;
    const step = Math.min(length, distance);
    this.anchor.addScaledVector(scratch.divideScalar(length), step);
    this.position.copy(this.anchor);
  }

  takeDamage(amount: number): void {
    if (this.isDestroyed) return;
    this.health.takeDamage(amount);
    if (this.health.isDestroyed) this.destroyed = true;
  }

  /** Move the drone to a new anchor, keeping its hover behaviour. */
  setAnchor(position: Vector3): void {
    this.anchor.copy(position);
  }

  /**
   * Pin the drone at a fixed world position, with no patrol, bob or drift.
   * Used by the QA hooks so a scripted shot can be aimed deterministically.
   */
  holdAt(position: Vector3): void {
    this.anchor.copy(position);
    this.position.copy(position);
    this.patrolRadius = 0;
    this.drift.set(0, 0, 0);
    this.held = true;
  }

  /**
   * Advance the drone. Returns true when it wants to fire one bolt at the
   * player. `canSeeTarget` is the continuous line-of-sight check from the
   * controller; the drone keeps its own burst/rest cadence and reacts to a
   * target that comes and goes.
   */
  update(time: number, dt: number, canSeeTarget = true): boolean {
    if (this.isDestroyed) return false;
    if (this.netTimer > 0) {
      this.netTimer = Math.max(0, this.netTimer - dt);
      // A netted drone is immobilised and disarmed.
      return false;
    }

    this.move(time, dt);
    if (this.suspicionTimer > 0) this.suspicionTimer = Math.max(0, this.suspicionTimer - dt);

    if (!canSeeTarget) {
      // A heard noise keeps the drone awake and ready, but it cannot fire
      // through a wall. With no suspicion it drops back to rest as before.
      if (this.suspicionTimer > 0) {
        this.targetSeen = false;
        this.state = 'alert';
        this.burstShots = 0;
        this.restTimer = 0;
        return false;
      }
      // Lose the target: stop firing and hold until it is seen again.
      this.targetSeen = false;
      this.state = 'rest';
      this.burstShots = 0;
      this.restTimer = 0;
      this.reacquireTimer = this.profile.reacquireDelay;
      return false;
    }

    if (!this.targetSeen) {
      // Newly acquired: wake up after the reaction time (plus a re-acquire
      // pause if the target had been lost). A heard noise shortens the wake-up
      // because the drone already knows something is there.
      this.targetSeen = true;
      this.state = 'alert';
      const reaction =
        this.suspicionTimer > 0
          ? BALANCE.stealth.hearingReaction
          : Math.max(this.restTimer, this.profile.reactTime);
      this.reactTimer = reaction + Math.max(0, this.reacquireTimer);
      this.restTimer = 0;
      this.reacquireTimer = 0;
      return false;
    }

    switch (this.state) {
      case 'patrol':
      case 'rest': {
        this.restTimer -= dt;
        if (this.restTimer > 0) return false;
        this.state = 'alert';
        this.reactTimer = this.profile.reactTime;
        return false;
      }
      case 'alert': {
        this.reactTimer -= dt;
        if (this.reactTimer > 0) return false;
        this.state = 'engage';
        this.burstShots = this.profile.burstCount;
        this.burstLength = this.profile.burstCount;
        this.burstTimer = 0;
        return false;
      }
      case 'engage': {
        this.burstTimer -= dt;
        if (this.burstTimer > 0) return false;
        this.burstTimer += this.profile.burstInterval;
        // The spread tightens along the burst; on a chaotic tier the shot takes
        // a random point on that ramp instead of the next step along it, so the
        // pattern cannot be read for a lean dodge.
        this.shotSpread = burstSpread(
          this.profile,
          this.burstLength - this.burstShots,
          this.burstLength,
          this.fireRng.next(),
        );
        this.burstShots -= 1;
        if (this.burstShots <= 0) {
          this.state = 'rest';
          this.restTimer = this.profile.burstRest;
        }
        return true;
      }
    }
  }

  /** Hover, patrol and bob. Split out so the combat state stays readable. */
  private move(time: number, dt: number): void {
    if (this.held) return;
    if (this.route) this.advanceRoute(dt);
    const up = this.surface ? this.surface.normal : scratch.set(0, 1, 0);
    const bob = Math.sin(time * this.bobSpeed + this.phase) * this.bobAmount;

    if (this.surface && this.patrolRadius > 0.01) {
      // Accumulate the angle so switching into the faster engage orbit never
      // jumps the drone to another point on the circle.
      const engageBoost = this.state === 'engage' ? 2 : 1;
      this.patrolAngle = wrapAngle(this.patrolAngle + dt * this.patrolSpeed * engageBoost);
      scratchOrbit
        .copy(this.surface.right)
        .multiplyScalar(Math.cos(this.patrolAngle) * this.patrolRadius)
        .addScaledVector(this.surface.forward, Math.sin(this.patrolAngle) * this.patrolRadius);
    } else {
      scratchOrbit.set(0, 0, 0);
    }

    this.position
      .copy(this.anchor)
      .add(scratchOrbit)
      .addScaledVector(up, BALANCE.combat.droneHoverHeight + bob)
      .add(this.drift);
  }

  /**
   * Fly the anchor toward the next waypoint.
   *
   * The anchor is what moves, so the hover height and bob stay relative to the
   * surface the drone was placed on. A drone that is watching or fighting the
   * player holds station instead: a route is a sweep, not a chase.
   */
  private advanceRoute(dt: number): void {
    const route = this.route;
    if (!route || route.length < 2 || this.isAlerted) return;
    const target = route[this.routeIndex % route.length]!;
    scratch.subVectors(target, this.anchor);
    const distance = scratch.length();
    const step = BALANCE.combat.droneRouteSpeed * dt;
    if (distance <= step || distance < 1e-4) {
      this.anchor.copy(target);
      this.routeIndex = (this.routeIndex + 1) % route.length;
      return;
    }
    this.anchor.addScaledVector(scratch.divideScalar(distance), step);
  }

  /**
   * The spread for the shot about to be fired, for tests and tooling.
   */
  get aimSpread(): number {
    return this.shotSpread;
  }

  /**
   * A deterministic spread applied to the shot so the player can dodge a bolt
   * instead of having to predict it exactly.
   *
   * The size comes from the burst's tighten ramp (`shotSpread`). A chaotic tier
   * adds a vertical scatter on top, so the bolts do not all arrive on one line
   * and a lean dodge has to be reactive rather than remembered.
   */
  applyAimJitter(direction: Vector3, out = new Vector3()): Vector3 {
    const spread = this.shotSpread;
    if (spread <= 0) return out.copy(direction);

    const angle = this.fireRng.range(-spread, spread);
    // Rotate the aim around the world up, which is the horizontal spread.
    out.set(
      direction.x * Math.cos(angle) - direction.z * Math.sin(angle),
      direction.y,
      direction.x * Math.sin(angle) + direction.z * Math.cos(angle),
    );

    const chaos = this.profile.jitterChaos;
    if (chaos > 0) {
      const vertical = this.fireRng.range(-spread * chaos, spread * chaos);
      // Rotate about the shot's own horizontal axis: `right` is the axis the
      // aim turns around when it rises or falls.
      scratchJitterRight.crossVectors(out, UP);
      if (vertical !== 0 && scratchJitterRight.lengthSq() > 1e-8) {
        scratchJitterRight.normalize();
        scratch.crossVectors(scratchJitterRight, out);
        const cos = Math.cos(vertical);
        const sin = Math.sin(vertical);
        out.multiplyScalar(cos).addScaledVector(scratch, sin);
      }
    }

    return out.normalize();
  }
}
